import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Box, Chip, Skeleton, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  AssignmentTurnedInRounded,
  AttachMoneyRounded,
  BarChartRounded,
  MoneyOffRounded,
  PercentRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import ReportMetricCard from "components/reports/shared/ReportMetricCard";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import { getChartColors } from "theme";
import DashboardV2Template from "./DashboardV2Template";

// ─── helpers ────────────────────────────────────────────────────────────────

const MONTH_MAP = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

const fmt = (value, type) => {
  const n = Number(value || 0);
  if (type === "Currency")
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  if (type === "Percentage") return `${n.toFixed(1)}%`;
  return new Intl.NumberFormat("en-US").format(n);
};

const pctChange = (prev, curr) => {
  const p = Number(prev || 0);
  const c = Number(curr || 0);
  if (p === 0) return c === 0 ? 0 : 100;
  return parseFloat((((c - p) / p) * 100).toFixed(2));
};

// ─── pie chart helper ────────────────────────────────────────────────────────

// ─── KPI skeleton row ────────────────────────────────────────────────────────

const KpiSkeleton = () => (
  <Grid container spacing={2} size={{ xs: 12 }}>
    {[...Array(5)].map((_, i) => (
      <Grid key={i} size={{ xs: 6, sm: 6, md: 4, lg: 12 / 5 }}>
        <Skeleton variant="rounded" height={110} sx={{ borderRadius: "12px" }} animation="wave" />
      </Grid>
    ))}
  </Grid>
);

// ─── main component ──────────────────────────────────────────────────────────

const DashboardV2AllGeneral = ({ dateSelection }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const chartColors = getChartColors(theme);
  const { refreshPage, handleErrorDialog } = useStateContext();

  const selectedYear = dateSelection?.years?.[0] || dayjs().year();
  const selectedMonths = useMemo(() => dateSelection?.months || [], [dateSelection?.months]);
  const selectedMonthNumbers = useMemo(
    () => selectedMonths.map((m) => MONTH_MAP[m] || 1),
    [selectedMonths],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [pies, setPies] = useState(null);
  const [statusBars, setStatusBars] = useState(null);
  const [revenueClients, setRevenueClients] = useState(null);
  const [ordersClients, setOrdersClients] = useState(null);
  const DRAWER_INITIAL = { open: false, loading: false, title: "", subtitle: "", data: [], columns: [], summary: null, onRowClick: null };
  const [drillDown, setDrillDown] = useState(DRAWER_INITIAL);
  const closeDrillDown = () => setDrillDown(DRAWER_INITIAL);

  // ── comparison periods ────────────────────────────────────────────────────
  const comparison = useMemo(() => {
    const sorted = [...selectedMonthNumbers].sort((a, b) => a - b);
    const n = sorted.length;
    if (n === 0 || n === 1) {
      const mo = n === 1 ? sorted[0] : dayjs().month() + 1;
      const yr = selectedYear;
      const prevDate = dayjs(`${yr}-${mo}-01`).subtract(1, "month");
      return {
        current: { label: dayjs(`${yr}-${mo}-01`).format("MMM YYYY"), period: { month: mo, year: yr }, type: "month" },
        previous: { label: prevDate.format("MMM YYYY"), period: { month: prevDate.month() + 1, year: prevDate.year() } },
      };
    }
    const first = sorted[0], last = sorted[n - 1];
    const prevMonths = sorted.map((m) => dayjs(`${selectedYear}-${m}-01`).subtract(n, "month").month() + 1);
    const prevYear = dayjs(`${selectedYear}-${first}-01`).subtract(n, "month").year();
    const cLabel = `${dayjs(`${selectedYear}-${first}-01`).format("MMM")}–${dayjs(`${selectedYear}-${last}-01`).format("MMM")} ${selectedYear}`;
    const pLabel = `${dayjs(`${prevYear}-${prevMonths[0]}-01`).format("MMM")}–${dayjs(`${prevYear}-${prevMonths[n - 1]}-01`).format("MMM")} ${prevYear}`;
    return {
      current: { label: cLabel, period: { months: sorted, year: selectedYear } },
      previous: { label: pLabel, period: { months: prevMonths, year: prevYear } },
    };
  }, [selectedMonthNumbers, selectedYear]);

  const paramsKPI = useMemo(() => {
    if (comparison.current.period.month !== undefined) {
      return { thisMonth: comparison.current.period, lastMonth: comparison.previous.period };
    }
    return {
      currentPeriod: comparison.current.period,
      previousPeriod: comparison.previous.period,
      comparisonType: "period",
    };
  }, [comparison]);

  const paramsDate = useMemo(
    () => ({ year: selectedYear, months: selectedMonthNumbers }),
    [selectedYear, selectedMonthNumbers],
  );

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setError(null);
    api.all([
      api.get("/dashboard/all/generalOverview/kpis", { params: paramsKPI }),
      api.get("/dashboard/all/generalOverview/kpisPieData", { params: paramsDate }),
      api.get("/dashboard/all/generalOverview/serviceStatusBarData", { params: paramsDate }),
      api.get("/dashboard/all/generalOverview/topClientsByRevenue", { params: paramsDate }),
      api.get("/dashboard/all/generalOverview/topClientsByOrders", { params: paramsDate }),
    ])
      .then(api.spread((k, p, b, r, o) => {
        setKpis(k?.data ?? null);
        setPies(p?.data ?? null);
        setStatusBars(b?.data ?? null);
        setRevenueClients(r?.data ?? null);
        setOrdersClients(o?.data ?? null);
        setIsLoading(false);
      }))
      .catch((err) => {
        const msg = err.response?.data?.message || "Failed to load dashboard data.";
        setError(msg);
        handleErrorDialog(msg, theme);
        setIsLoading(false);
      });
  }, [paramsKPI, paramsDate, handleErrorDialog, theme]);

  useEffect(() => { fetchData(); }, [fetchData, refreshPage]);

  // ── helpers that depend on fetched data ───────────────────────────────────
  const curr = (key) =>
    kpis?.currentPeriod?.[key] ?? kpis?.thisMonth?.[key] ?? 0;
  const prev = (key) =>
    kpis?.previousPeriod?.[key] ?? kpis?.lastMonth?.[key] ?? 0;

  const transformToPie = (barData, dataKeys) => {
    if (!barData?.length) return [];
    const row = barData[0];
    return dataKeys
      .filter((k) => row[k.dataKey] > 0)
      .map((k) => ({ id: k.label, value: row[k.dataKey], label: k.label, color: k.color }));
  };

  const interpBarRow = useMemo(() => [{
    requested: statusBars?.Interpretation?.requested || 0,
    confirmed: statusBars?.Interpretation?.confirmed || 0,
    cancelled: statusBars?.Interpretation?.cancelled || 0,
    completed: statusBars?.Interpretation?.completed || 0,
  }], [statusBars]);

  const transBarRow = useMemo(() => [{
    inProgress: statusBars?.Translation?.inProgress || 0,
    deliveredCompleted: statusBars?.Translation?.deliveredCompleted || 0,
  }], [statusBars]);

  const otherBarRow = useMemo(() => [{
    inProgress: statusBars?.Other?.inProgress || 0,
    deliveredCompleted: statusBars?.Other?.deliveredCompleted || 0,
  }], [statusBars]);

  const interpPie = transformToPie(interpBarRow, [
    { dataKey: "requested", label: "Requested", color: chartColors.series[3] },
    { dataKey: "confirmed", label: "Confirmed", color: chartColors.series[0] },
    { dataKey: "cancelled", label: "Cancelled", color: chartColors.series[4] },
    { dataKey: "completed", label: "Completed", color: chartColors.series[2] },
  ]);

  const transPie = transformToPie(transBarRow, [
    { dataKey: "inProgress", label: "In-Progress", color: chartColors.series[3] },
    { dataKey: "deliveredCompleted", label: "Delivered / Completed", color: chartColors.series[0] },
  ]);

  const otherPie = transformToPie(otherBarRow, [
    { dataKey: "inProgress", label: "In-Progress", color: chartColors.series[3] },
    { dataKey: "deliveredCompleted", label: "Delivered / Completed", color: chartColors.series[0] },
  ]);

  const revenueClientData = useMemo(() => {
    if (!revenueClients?.accounts) return [];
    return revenueClients.accounts.map((name, i) => ({
      name,
      interpretation: revenueClients.interpretation?.[i] || 0,
      translation: revenueClients.translation?.[i] || 0,
      other: revenueClients.other?.[i] || 0,
    }));
  }, [revenueClients]);

  const ordersClientData = useMemo(() => {
    if (!ordersClients?.accounts) return [];
    return ordersClients.accounts.map((name, i) => ({
      name,
      interpretation: ordersClients.interpretation?.[i] || 0,
      translation: ordersClients.translation?.[i] || 0,
      other: ordersClients.other?.[i] || 0,
    }));
  }, [ordersClients]);

  const clientBarSeries = [
    { dataKey: "interpretation", label: "Interpretation", color: chartColors.series[0] },
    { dataKey: "translation", label: "Translation", color: chartColors.series[1] },
    { dataKey: "other", label: "Other Services", color: chartColors.series[2] },
  ];

  const SERVICE_COLUMNS = [
    { field: "projectNumber", headerName: "#", width: 80 },
    {
      field: "date",
      headerName: "Date",
      width: 110,
      renderCell: (row) => row.date ? dayjs(row.date).format("MM/DD/YYYY") : "-",
    },
    { field: "name", headerName: "Name", width: 180 },
    { field: "client", headerName: "Client", width: 180 },
    { field: "linguist", headerName: "Linguist", width: 160 },
    { field: "serviceType", headerName: "Type", width: 160 },
    { field: "status", headerName: "Status", width: 140 },
    {
      field: "accountCharge",
      headerName: "Charge",
      width: 110,
      renderCell: (row) => `$${Number(row.accountCharge || 0).toFixed(2)}`,
    },
    {
      field: "totalCost",
      headerName: "Cost",
      width: 110,
      renderCell: (row) => `$${Number(row.totalCost || 0).toFixed(2)}`,
    },
    {
      field: "profit",
      headerName: "Profit",
      width: 110,
      renderCell: (row) => `$${Number(row.profit || 0).toFixed(2)}`,
    },
    {
      field: "margin",
      headerName: "Margin",
      width: 100,
      renderCell: (row) => `${Number(row.margin || 0).toFixed(1)}%`,
    },
  ];

  const openServiceProfileFromRow = (row) => {
    if (!row?.id) return;
    navigate(`/services/${row.id}`, { state: { ComingFrom: "/dashboard" } });
  };

  const fetchAndOpenGeneralDrillDown = (meta, apiParams = {}) => {
    setDrillDown({
      ...DRAWER_INITIAL,
      open: true,
      loading: true,
      onRowClick: openServiceProfileFromRow,
      ...meta,
    });

    api
      .get("/dashboard/all/services/servicesByStatus", { params: { ...paramsDate, ...apiParams } })
      .then((res) => {
        setDrillDown((prev) => ({ ...prev, loading: false, data: res?.data || [], columns: SERVICE_COLUMNS }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.response?.data?.message || "Unable to load drill-down data.";
        handleErrorDialog(msg, theme);
        setDrillDown((prev) => ({ ...prev, loading: false }));
      });
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <DashboardV2Template error={error} loading={false}>
      {/* Period comparison badge */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, mb: 2 }}>
        <Chip
          label={comparison.current.label}
          size="small"
          color="primary"
          variant="filled"
          sx={{ fontWeight: 700, borderRadius: "8px" }}
        />
        <Typography variant="body2" color="text.secondary">vs</Typography>
        <Chip
          label={comparison.previous.label}
          size="small"
          variant="outlined"
          sx={{ borderRadius: "8px" }}
        />
      </Box>

      {/* ── KPI row ───────────────────────────────────────────────────────── */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Key Performance Indicators</Typography>
      {isLoading ? (
        <KpiSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            {
              title: "Total Services",
              key: "totalServices",
              type: "Number",
              icon: <AssignmentTurnedInRounded />,
            },
            {
              title: "Account Charge",
              key: "totalAccountCharge",
              type: "Currency",
              icon: <AttachMoneyRounded />,
            },
            {
              title: "Total Cost",
              key: "totalTotalCost",
              type: "Currency",
              reverse: true,
              icon: <MoneyOffRounded />,
            },
            {
              title: "Profit",
              key: "profit",
              type: "Currency",
              icon: <BarChartRounded />,
            },
            {
              title: "Service Margin",
              key: "margin",
              type: "Percentage",
              icon: <PercentRounded />,
            },
          ].map(({ title, key, type, reverse, icon }) => {
            const currentVal = curr(key);
            const previousVal = prev(key);
            const change = pctChange(previousVal, currentVal);
            const trendVal = reverse ? -change : change;
              const pieData = [
                { id: 0, value: Number(pies?.Interpretation?.[key] || 0), label: "Interpretation" },
                { id: 1, value: Number(pies?.Translation?.[key] || 0), label: "Translation" },
                { id: 2, value: Number(pies?.Other?.[key] || 0), label: "Other Services" },
              ];
            const hasBreakdown = pieData.some((d) => d.value > 0);

            return (
              <Grid key={key} size={{ xs: 6, sm: 6, md: 4, lg: 12 / 5 }}>
                <ReportMetricCard
                  title={title}
                  value={fmt(currentVal, type)}
                  subtitle={`${comparison.previous.label}: ${fmt(previousVal, type)}`}
                  trend={trendVal}
                  trendLabel={`vs ${comparison.previous.label}`}
                  icon={icon}
                  color={
                    type === "Currency" && key !== "totalTotalCost"
                      ? theme.palette.primary.main
                      : undefined
                  }
                  tooltipTitle={title}
                  tooltipContent={
                    hasBreakdown
                      ? pieData
                          .filter((d) => d.value > 0)
                          .map((d) => `${d.label}: ${fmt(d.value, type)}`)
                          .join("\n")
                      : undefined
                  }
                  onClick={() =>
                    fetchAndOpenGeneralDrillDown(
                      {
                        title,
                        subtitle: comparison.current.label,
                        summary: {
                          metrics: [
                            { label: "Current", value: fmt(currentVal, type), color: theme.palette.primary.main },
                            { label: "Previous", value: fmt(previousVal, type), color: theme.palette.info.main },
                            { label: "Change", value: `${trendVal > 0 ? "+" : ""}${trendVal.toFixed(1)}%` },
                          ],
                        },
                      },
                      {},
                    )
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Service status breakdown ──────────────────────────────────────── */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Service Status Breakdown</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { title: "Interpretation Status", data: interpPie, category: "Interpretation", filterMode: "status" },
          { title: "Translation Status", data: transPie, category: "Translation", filterMode: "status" },
          { title: "Other Services Status", data: otherPie, category: "Other", filterMode: "status" },
        ].map(({ title, data: pieData, category, filterMode }) => (
          <Grid key={title} size={{ xs: 12, sm: 12, md: 4 }}>
            <ReportPieChart
              title={title}
              data={pieData}
              height={320}
              legendPosition="bottom"
              onSliceClick={(slice) => {
                const total = pieData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
                const pct = total > 0 ? ((Number(slice.value) / total) * 100).toFixed(1) : "0.0";
                fetchAndOpenGeneralDrillDown(
                  {
                    title,
                    subtitle: `${slice.label} — ${fmt(slice.value)} (${pct}%)`,
                    summary: {
                      metrics: [
                        { label: "Status", value: slice.label },
                        { label: "Count", value: fmt(slice.value), color: theme.palette.primary.main },
                        { label: "Share", value: `${pct}%`, color: theme.palette.info.main },
                      ],
                    },
                  },
                  { category, [filterMode]: slice.label },
                );
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Service mix pies ──────────────────────────────────────────────── */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Service Mix by Category</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { title: "Services Mix", key: "totalServices", type: "Number" },
          { title: "Revenue Mix", key: "totalAccountCharge", type: "Currency" },
          { title: "Profit Mix", key: "profit", type: "Currency" },
        ].map(({ title, key, type }) => {
          const mixData = [
            { id: 0, value: Number(pies?.Interpretation?.[key] || 0), label: "Interpretation", category: "Interpretation" },
            { id: 1, value: Number(pies?.Translation?.[key] || 0), label: "Translation", category: "Translation" },
            { id: 2, value: Number(pies?.Other?.[key] || 0), label: "Other Services", category: "Other" },
          ];
          return (
            <Grid key={key} size={{ xs: 12, sm: 12, md: 4 }}>
              <ReportPieChart
                title={title}
                data={mixData}
                height={320}
                legendPosition="bottom"
                valueFormatter={(v) => fmt(v, type)}
                onSliceClick={(slice) => {
                  const matched = mixData.find((d) => d.label === slice.label);
                  fetchAndOpenGeneralDrillDown(
                    {
                      title: `${title} — ${slice.label}`,
                      subtitle: comparison.current.label,
                      summary: {
                        metrics: [
                          { label: "Category", value: slice.label },
                          { label: "Value", value: fmt(slice.value, type), color: theme.palette.primary.main },
                        ],
                      },
                    },
                    { category: matched?.category || slice.label },
                  );
                }}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* ── Top clients ───────────────────────────────────────────────────── */}
      <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 1 }}>Top Clients</Typography>
      <Grid container spacing={2} sx={{ pb: 2 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ReportBarChart
            title="Account Charges by Client"
            data={revenueClientData}
            xAxisKey="name"
            series={clientBarSeries}
            height={380}
            valueFormatter={(v) => fmt(v, "Currency")}
            chartMargin={{ top: 20, right: 20, bottom: 100, left: 80 }}
            onBarClick={(item) =>
              fetchAndOpenGeneralDrillDown(
                {
                  title: `Account Charges — ${item.name}`,
                  subtitle: comparison.current.label,
                  summary: {
                    metrics: [
                      { label: "Client", value: item.name },
                      { label: "Interpretation", value: fmt(item.interpretation, "Currency"), color: theme.palette.info.main },
                      { label: "Translation", value: fmt(item.translation, "Currency"), color: theme.palette.warning.main },
                      { label: "Other", value: fmt(item.other, "Currency"), color: theme.palette.success.main },
                    ],
                  },
                },
                { clientName: item.name },
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ReportBarChart
            title="Number of Orders by Client"
            data={ordersClientData}
            xAxisKey="name"
            series={clientBarSeries}
            height={380}
            valueFormatter={(v) => fmt(v, "Number")}
            chartMargin={{ top: 20, right: 20, bottom: 100, left: 80 }}
            onBarClick={(item) =>
              fetchAndOpenGeneralDrillDown(
                {
                  title: `Orders — ${item.name}`,
                  subtitle: comparison.current.label,
                  summary: {
                    metrics: [
                      { label: "Client", value: item.name },
                      { label: "Interpretation", value: fmt(item.interpretation), color: theme.palette.info.main },
                      { label: "Translation", value: fmt(item.translation), color: theme.palette.warning.main },
                      { label: "Other", value: fmt(item.other), color: theme.palette.success.main },
                    ],
                  },
                },
                { clientName: item.name },
              )
            }
          />
        </Grid>
      </Grid>

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
        onRowClick={drillDown.onRowClick}
        loading={drillDown.loading}
      />
    </DashboardV2Template>
  );
};

export default DashboardV2AllGeneral;
